/**
 * ChessOffline - Play chess vs Stockfish entirely client-side
 *
 * Features:
 * - Fully offline capable
 * - Adjustable difficulty (beginner to master)
 * - Uses Stockfish 17.1 WASM engine
 */

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ChessContainer from './ChessContainer';
import { useLocalChessGame, DIFFICULTY_PRESETS, DifficultyLevel } from './hooks';
import './Chess.css';
import './ChessOffline.css';

function ChessOffline() {
    const navigate = useNavigate();
    const [playerColor, setPlayerColor] = useState<'white' | 'black'>('white');
    const [selectedDifficulty, setSelectedDifficulty] = useState<DifficultyLevel>('medium');
    const [gameStarted, setGameStarted] = useState(false);
    const [showGameOver, setShowGameOver] = useState(false);
    const [gameResult, setGameResult] = useState<{ result: 'win' | 'lose' | 'draw'; reason: string } | null>(null);

    const handleGameOver = useCallback((result: 'win' | 'lose' | 'draw', reason: string) => {
        setGameResult({ result, reason });
        setShowGameOver(true);
    }, []);

    const {
        game,
        isPlayerTurn,
        isThinking,
        isEngineReady,
        difficulty,
        setDifficulty,
        makeMove,
        resetGame,
        undoMove,
        gameStatus,
        isCheck
    } = useLocalChessGame({
        playerColor,
        difficulty: selectedDifficulty,
        onGameOver: handleGameOver
    });

    const handleMakeMove = useCallback((from: { row: number; col: number }, to: { row: number; col: number }) => {
        makeMove(from, to);
    }, [makeMove]);

    const startGame = () => {
        setGameStarted(true);
        setShowGameOver(false);
        setGameResult(null);
        resetGame();
    };

    const handleNewGame = () => {
        setGameStarted(false);
        setShowGameOver(false);
        setGameResult(null);
    };

    const handlePlayAgain = () => {
        setShowGameOver(false);
        setGameResult(null);
        resetGame();
    };

    // Pre-game setup screen
    if (!gameStarted) {
        return (
            <div className="game-container">
                <div className="chess-offline-setup">
                    <h1>Play vs Computer</h1>
                    <p className="subtitle">Powered by Stockfish 17.1</p>

                    {!isEngineReady ? (
                        <div className="loading-engine">
                            <div className="spinner"></div>
                            <p>Loading chess engine...</p>
                        </div>
                    ) : (
                        <>
                            <div className="setup-section">
                                <h3>Play as:</h3>
                                <div className="color-selector">
                                    <button
                                        className={`color-btn ${playerColor === 'white' ? 'active' : ''}`}
                                        onClick={() => setPlayerColor('white')}
                                    >
                                        <span className="color-icon white"></span>
                                        White
                                    </button>
                                    <button
                                        className={`color-btn ${playerColor === 'black' ? 'active' : ''}`}
                                        onClick={() => setPlayerColor('black')}
                                    >
                                        <span className="color-icon black"></span>
                                        Black
                                    </button>
                                </div>
                            </div>

                            <div className="setup-section">
                                <h3>Difficulty:</h3>
                                <div className="difficulty-selector">
                                    {(Object.keys(DIFFICULTY_PRESETS) as DifficultyLevel[]).map(level => (
                                        <button
                                            key={level}
                                            className={`difficulty-btn ${selectedDifficulty === level ? 'active' : ''}`}
                                            onClick={() => setSelectedDifficulty(level)}
                                        >
                                            {level.charAt(0).toUpperCase() + level.slice(1)}
                                        </button>
                                    ))}
                                </div>
                                <p className="difficulty-hint">
                                    {selectedDifficulty === 'beginner' && 'Perfect for learning the basics'}
                                    {selectedDifficulty === 'easy' && 'Casual play, makes some mistakes'}
                                    {selectedDifficulty === 'medium' && 'Solid play, good challenge'}
                                    {selectedDifficulty === 'hard' && 'Strong play, few mistakes'}
                                    {selectedDifficulty === 'expert' && 'Very strong, tournament level'}
                                    {selectedDifficulty === 'master' && 'Full Stockfish strength'}
                                </p>
                            </div>

                            <button className="start-game-btn" onClick={startGame}>
                                Start Game
                            </button>
                        </>
                    )}

                    <button className="back-button" onClick={() => navigate('/mferchess')}>
                        Back to Chess
                    </button>
                </div>
            </div>
        );
    }

    // Game screen
    return (
        <div className="game-container">
            <div className="title-section">
                <h1>vs Stockfish ({difficulty})</h1>
                <div className="game-status-bar">
                    {isThinking && <span className="thinking-indicator">Thinking...</span>}
                    {isCheck && gameStatus === 'playing' && <span className="check-indicator">Check!</span>}
                    {isPlayerTurn && !isThinking && gameStatus === 'playing' && (
                        <span className="turn-indicator">Your turn</span>
                    )}
                </div>
            </div>

            <div className="board-section">
                <div className="board-wrapper">
                    <ChessContainer
                        game={game}
                        playerId="player"
                        makeMove={handleMakeMove}
                    />
                </div>
            </div>

            <div className="game-controls">
                <button onClick={undoMove} disabled={(game.moves?.length ?? 0) < 2 || isThinking}>
                    Undo
                </button>
                <button onClick={handleNewGame}>
                    New Game
                </button>
            </div>

            <button className="back-button" onClick={() => navigate('/mferchess')}>
                Back to Chess
            </button>

            {/* Game Over Modal */}
            {showGameOver && gameResult && (
                <div className="modal-overlay" onClick={() => setShowGameOver(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <h2>
                            {gameResult.result === 'win' && 'You Won!'}
                            {gameResult.result === 'lose' && 'You Lost'}
                            {gameResult.result === 'draw' && 'Draw'}
                        </h2>
                        <p>by {gameResult.reason}</p>
                        <div className="modal-buttons">
                            <button className="confirm-button" onClick={handlePlayAgain}>
                                Play Again
                            </button>
                            <button className="cancel-button" onClick={handleNewGame}>
                                Change Settings
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ChessOffline;
